/**
 * Centre d'export transverse — hooks TanStack Query (DOC-003bis Phase 5).
 *
 * Liste des exports DU user (polling tant qu'un job est en cours), suppression,
 * relance, acquittement (RAZ pastille) et compteur « non consultés » (nav badge).
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import * as exportsApi from '@/lib/api/exports'
import { direLeRefusDUnGesteDExport } from '@/hooks/refusDUnGesteDExport'
import type { ExportJob, ExportListCursor, ExportListResponse } from '@/hooks/useExports.types'
import { queryKeys } from '@/lib/queryKeys'

const IN_PROGRESS: ReadonlySet<string> = new Set(['queued', 'processing', 'deferred'])

function hasInProgressPages(data: { pages: ExportListResponse[] } | undefined): boolean {
    return !!data?.pages.some((p) => p.items.some((j) => IN_PROGRESS.has(j.status)))
}

/** Liste paginée (keyset, 50/page) des exports DU user, avec « charger plus ». */
export function useExports() {
    return useInfiniteQuery({
        queryKey: queryKeys.exports.list(),
        queryFn: ({ pageParam }) => exportsApi.fetchExports(pageParam),
        initialPageParam: null as ExportListCursor | null,
        getNextPageParam: (lastPage: ExportListResponse) => lastPage.next_cursor ?? undefined,
        // Polling léger UNIQUEMENT s'il reste un export en cours (toast à l'arrivée).
        refetchInterval: (query) =>
            hasInProgressPages(query.state.data as { pages: ExportListResponse[] } | undefined)
                ? 4000
                : false,
        staleTime: 10 * 1000,
    })
}

export function useExportsUnseenCount(enabled = true) {
    return useQuery({
        queryKey: queryKeys.exports.unseenCount(),
        queryFn: () => exportsApi.fetchExportsUnseenCount(),
        enabled,
        refetchInterval: 60 * 1000,
        staleTime: 30 * 1000,
    })
}

/**
 * Téléchargement d'un export → EFFACE la pastille (politique PO Lot F : la
 * pastille s'efface au TÉLÉCHARGEMENT, plus à la visite de la page). Invalide la
 * liste (apparition du statut « Téléchargé ») ET le compteur de pastille.
 */
export function useDownloadExport() {
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    return useMutation({
        // Le NOM que la liste porte (`file_name`, calculé par le serveur : date de
        // mise à disposition, fuseau de l'organisme). Sans lui, un `/download-url`
        // en échec retombait sur le miroir (date de DEMANDE, fuseau du POSTE) : un
        // export terminé le lendemain de sa demande changeait de nom.
        mutationFn: (job: Pick<ExportJob, 'id' | 'label' | 'created_at' | 'format' | 'file_name'>) =>
            exportsApi.downloadExport(job.id, job.label, job.created_at, job.format, job.file_name),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.exports.all })
        },
        // Un droit manquant se dit dans la langue de l'écran, par son code.
        onError: (e) => direLeRefusDUnGesteDExport(e, t('exports.toast.download_error'), t),
    })
}

/**
 * Télécharge UN ou TOUS les volumes d'un export multi-volumes (séquentiellement).
 * Une seule invalidation à la fin (la pastille s'efface quand TOUS sont récupérés).
 */
export function useDownloadVolumes() {
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    return useMutation({
        mutationFn: async ({
            job,
            seqs,
        }: {
            job: Pick<ExportJob, 'id' | 'label' | 'created_at' | 'volumes'>
            seqs: number[]
        }) => {
            for (const seq of seqs) {
                // Le nom du SERVEUR pour ce volume (la liste le porte) : le
                // dériver ici du libellé, c'était l'enregistrer sous un autre nom.
                const nom = job.volumes?.find((v) => v.seq === seq)?.file_name
                await exportsApi.downloadExportVolume(job.id, seq, job.label, job.created_at, nom)
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.exports.all })
        },
        // Un droit manquant se dit dans la langue de l'écran, par son code.
        onError: (e) => direLeRefusDUnGesteDExport(e, t('exports.toast.download_error'), t),
    })
}

export function useDeleteExport() {
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    return useMutation({
        mutationFn: (jobId: string) => exportsApi.deleteExportJob(jobId),
        onSuccess: () => {
            toast.success(t('exports.toast.deleted'))
            queryClient.invalidateQueries({ queryKey: queryKeys.exports.all })
        },
        onError: (e) => direLeRefusDUnGesteDExport(e, t('exports.toast.delete_error'), t),
    })
}

export function useRelaunchExport() {
    const queryClient = useQueryClient()
    const { t, i18n } = useTranslation()
    return useMutation({
        mutationFn: (jobId: string) => exportsApi.relaunchExportJob(jobId, i18n.language),
        onSuccess: (res) => {
            if (res.mode === 'sync') {
                // Le FORMAT du job : sans lui, le repli proxifié nommait `.zip` un
                // classeur relancé — et un tabulaire relancé part désormais en immédiat
                // jusqu'à 5 000 lignes, comme à sa création.
                // Le NOM rendu par la relance : le fichier relancé s'enregistre sous
                // le nom du serveur, le même qu'en production.
                void exportsApi.downloadExport(
                    res.job.id, res.job.label, res.job.created_at, res.job.format,
                    res.file_name,
                )
                toast.success(t('exports.toast.ready'))
            } else {
                toast.info(t('exports.toast.relaunched_async'))
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.exports.all })
        },
        onError: (e) => direLeRefusDUnGesteDExport(e, t('exports.toast.relaunch_error'), t),
    })
}

export type { ExportJob }
